import * as React from 'react'
import { EventField } from '@basehub/data/replicache/index'
import { GridCell, GridCellKind, Item } from '@glideapps/glide-data-grid'
import { isoStringToDisplayDate } from '~/lib/date-utils'
import { EventRow } from '~/app/api/v2/events/schema'
import { AtLeast } from '@basehub/utils/tree/index'
import { Action } from '../list/views/table/custom-cells/utils'
import { ExternalLinkIcon } from '@radix-ui/react-icons'

const DEFAULT_EMPTY_CELL: GridCell = {
  kind: GridCellKind.Text,
  data: '',
  allowOverlay: false,
  displayData: '',
  readonly: true,
}

export type BaseEventField = AtLeast<
  EventField,
  'name' | 'label' | 'type' | 'order'
>

export const DEFAULT_COLUMNS = [
  {
    label: 'Date',
    name: '_sys_timestamp',
    type: 'date',
    order: 0,
  },
] as const satisfies BaseEventField[]

export const useGetEventCellContent = ({
  rows,
  columns,
  isInitialLoading,
  protectedFilesToken,
}: {
  rows: EventRow[]
  columns: BaseEventField[]
  isInitialLoading: boolean
  protectedFilesToken: string | undefined
}) => {
  return React.useCallback(
    ([colNumber, rowNumber]: Item): GridCell => {
      if (isInitialLoading) {
        return {
          kind: GridCellKind.Loading,
          skeletonWidth: 100,
          skeletonHeight: 22,
          skeletonWidthVariability: 50,
          allowOverlay: false,
        }
      }
      const row = rows[rowNumber]
      if (!row) return DEFAULT_EMPTY_CELL

      const keyName = columns[colNumber]?.name
      if (!keyName) return DEFAULT_EMPTY_CELL
      let cellValue: unknown
      if (keyName.startsWith('_sys_')) {
        cellValue = row[keyName.replace('_sys_', '') as keyof EventRow]
      } else {
        cellValue = (row.value as Record<string, unknown>)?.[keyName]
      }
      if (cellValue === null || cellValue === undefined) {
        return DEFAULT_EMPTY_CELL
      }

      const currentColumn = columns[colNumber]
      if (!currentColumn) return DEFAULT_EMPTY_CELL

      switch (currentColumn.type) {
        case 'hidden':
        case 'text':
        case 'textarea':
        case 'email': {
          if (typeof cellValue !== 'string') return DEFAULT_EMPTY_CELL

          return {
            kind: GridCellKind.Text,
            data: cellValue,
            allowOverlay: true,
            displayData: cellValue.slice(0, 80),
            readonly: true,
          }
        }
        case 'radio':
        case 'select': {
          let parsed
          if (!currentColumn.multiple && typeof cellValue === 'string') {
            parsed = cellValue
          } else if (Array.isArray(cellValue)) {
            parsed = cellValue.join(', ')
          }

          if (typeof parsed !== 'string') return DEFAULT_EMPTY_CELL

          return {
            kind: GridCellKind.Text,
            data: parsed,
            allowOverlay: false,
            displayData: parsed.slice(0, 80),
            readonly: true,
          }
        }
        case 'checkbox': {
          if (typeof cellValue !== 'boolean') return DEFAULT_EMPTY_CELL
          return {
            kind: GridCellKind.Custom,
            copyData: cellValue.toString(),
            data: {
              value: cellValue,
              kind: 'boolean-cell',
              readonly: true,
            },
            allowOverlay: false,
          }
        }
        case 'number': {
          if (typeof cellValue !== 'number') return DEFAULT_EMPTY_CELL

          return {
            kind: GridCellKind.Number,
            data: cellValue,
            allowOverlay: false,
            displayData: cellValue.toString(),
            readonly: true,
          }
        }
        case 'date':
        case 'datetime': {
          if (typeof cellValue !== 'string') return DEFAULT_EMPTY_CELL

          let displayDate = ''
          try {
            if (!cellValue.includes('T')) {
              cellValue = new Date(Number(cellValue)).toISOString()
            }

            displayDate = cellValue
              ? isoStringToDisplayDate(cellValue as string, { useTime: true })
              : ''
          } catch (e) {
            console.error('Error parsing date:', e)
          }

          return {
            kind: GridCellKind.Custom,
            data: {
              date: cellValue,
              max: '9999-12-31',
              displayDate,
              format:
                currentColumn.type === 'datetime' ? 'datetime-local' : 'date',
              kind: 'date-picker-cell',
            },
            allowOverlay: false,
            copyData: displayDate,
          }
        }
        case 'file': {
          if (typeof cellValue !== 'string') return DEFAULT_EMPTY_CELL

          let url: URL
          try {
            url = new URL(cellValue)
            if (protectedFilesToken) {
              url.searchParams.set('token', protectedFilesToken)
            }
          } catch (e) {
            return DEFAULT_EMPTY_CELL
          }

          return {
            kind: GridCellKind.Custom,
            allowOverlay: false,
            activationBehaviorOverride: 'single-click',
            copyData: url.toString(),
            readonly: true,
            data: {
              actions: [
                {
                  name: 'open',
                  width: 24,
                  height: 24,
                  sprite: 'external',
                  icon: <ExternalLinkIcon width={14} height={14} />,
                  onClick: (url) => {
                    window.open(url, '_blank')
                  },
                },
              ] satisfies Action[],
              kind: 'media-preview-cell',
              value: url.toString(),
            },
          }
        }
        default: {
          currentColumn satisfies never
          return DEFAULT_EMPTY_CELL
        }
      }
    },
    [columns, isInitialLoading, protectedFilesToken, rows]
  )
}
